// Module Génération de Licences - Super Admin
import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiKey, FiCopy, FiCheck, FiRefreshCw, FiCalendar,
  FiUsers, FiShield, FiDownload, FiChevronLeft, FiCheckCircle
} from 'react-icons/fi';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

// Types
interface LicenseData {
  licenseKey: string;
  clientId: string;
  clientName: string;
  plan: 'starter' | 'professional' | 'premium' | 'enterprise';
  maxUsers: number;
  features: string[];
  validFrom: string;
  validUntil: string;
  generatedAt: string;
  generatedBy: string;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text}60;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
    color: ${theme.colors.primary};
  }

  svg {
    font-size: 24px;
  }
`;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0;
`;

const FormSection = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  svg {
    color: ${theme.colors.primary};
  }
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }

  &:disabled {
    background: ${theme.colors.surface}50;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.sm};
`;

const FeatureCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
  }

  input {
    cursor: pointer;
  }
`;

const GenerateButton = styled.button`
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.bold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};
  width: 100%;
  margin-top: ${theme.spacing.lg};

  &:hover {
    background: ${theme.colors.primary}DD;
    transform: translateY(-2px);
  }

  &:disabled {
    background: ${theme.colors.surface};
    color: ${theme.colors.text}60;
    cursor: not-allowed;
    transform: none;
  }
`;

const LicenseResult = styled.div`
  background: ${theme.colors.surface};
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
`;

const LicenseKeyDisplay = styled.div`
  background: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.lg};
  border: 2px dashed ${theme.colors.primary};
`;

const LicenseKeyText = styled.div`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  font-family: 'Courier New', monospace;
  text-align: center;
  letter-spacing: 2px;
  padding: ${theme.spacing.md};
  word-break: break-all;
`;

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

const ActionButton = styled.button`
  padding: ${theme.spacing.md};
  background: white;
  color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary};
    color: white;
  }
`;

const LicenseInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.md};
`;

const InfoItem = styled.div`
  background: white;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
`;

const InfoLabel = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  margin-bottom: ${theme.spacing.xs};
`;

const InfoValue = styled.div`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const SuccessMessage = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.success}20;
  border-left: 4px solid ${theme.colors.success};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.success};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.lg};
`;

export const LicenseGeneration: React.FC = () => {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedLicense, setGeneratedLicense] = useState<LicenseData | null>(null);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState({
    clientId: '',
    clientName: '',
    plan: 'professional' as 'starter' | 'professional' | 'premium' | 'enterprise',
    maxUsers: 20,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    features: ['scoring', 'commission', 'reporting'] as string[]
  });

  const availableFeatures = [
    { id: 'scoring', label: 'Scoring Automatique', default: true },
    { id: 'commission', label: 'Gestion Commissions', default: true },
    { id: 'reporting', label: 'Rapports & Analytics', default: true },
    { id: 'api', label: 'Accès API', default: false },
    { id: 'whitelabel', label: 'White Label', default: false },
    { id: 'support', label: 'Support Prioritaire', default: false },
    { id: 'customization', label: 'Personnalisation', default: false },
    { id: 'multisite', label: 'Multi-sites', default: false }
  ];

  const generateLicenseKey = () => {
    const prefix = formData.plan.substring(0, 3).toUpperCase();
    const year = new Date().getFullYear();
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `${prefix}-${year}-${random}`;
  };

  const handleGenerate = async () => {
    setIsGenerating(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    const license: LicenseData = {
      licenseKey: generateLicenseKey(),
      clientId: formData.clientId,
      clientName: formData.clientName,
      plan: formData.plan,
      maxUsers: formData.maxUsers,
      features: formData.features,
      validFrom: formData.validFrom,
      validUntil: formData.validUntil,
      generatedAt: new Date().toISOString(),
      generatedBy: 'Super Admin'
    };

    setGeneratedLicense(license);
    setIsGenerating(false);
  };

  const handleCopy = () => {
    if (generatedLicense) {
      navigator.clipboard.writeText(generatedLicense.licenseKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (generatedLicense) {
      const content = `
P-SCREEN LICENSE KEY
====================

Client: ${generatedLicense.clientName}
License Key: ${generatedLicense.licenseKey}
Plan: ${generatedLicense.plan.toUpperCase()}
Max Users: ${generatedLicense.maxUsers}
Valid From: ${new Date(generatedLicense.validFrom).toLocaleDateString('fr-FR')}
Valid Until: ${new Date(generatedLicense.validUntil).toLocaleDateString('fr-FR')}

Features:
${generatedLicense.features.map(f => `- ${f}`).join('\n')}

Generated: ${new Date(generatedLicense.generatedAt).toLocaleString('fr-FR')}
By: ${generatedLicense.generatedBy}

---
Pradium Tech - P-Screen Solution
      `.trim();

      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `license-${generatedLicense.licenseKey}.txt`;
      a.click();
    }
  };

  const toggleFeature = (featureId: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate('/superadmin/licenses')}>
              <FiChevronLeft />
            </BackButton>
            <div>
              <Title>Génération de Licence</Title>
              <Subtitle>Créer une nouvelle clé de licence client</Subtitle>
            </div>
          </HeaderLeft>
        </Header>

        <FormSection>
          <SectionTitle>
            <FiShield /> Configuration de la Licence
          </SectionTitle>

          <FormGrid>
            <FormGroup>
              <Label>
                <FiUsers /> ID Client
              </Label>
              <Input
                type="text"
                value={formData.clientId}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                placeholder="CLT-2024-XXXX"
              />
            </FormGroup>

            <FormGroup>
              <Label>Nom du Client</Label>
              <Input
                type="text"
                value={formData.clientName}
                onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                placeholder="Nom de l'organisation..."
              />
            </FormGroup>

            <FormGroup>
              <Label>Plan d'Abonnement</Label>
              <Select
                value={formData.plan}
                onChange={(e) => setFormData({
                  ...formData,
                  plan: e.target.value as any,
                  maxUsers: e.target.value === 'starter' ? 10 :
                           e.target.value === 'professional' ? 20 :
                           e.target.value === 'premium' ? 30 : 50
                })}
              >
                <option value="starter">Starter (10 utilisateurs)</option>
                <option value="professional">Professional (20 utilisateurs)</option>
                <option value="premium">Premium (30 utilisateurs)</option>
                <option value="enterprise">Enterprise (50+ utilisateurs)</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Nombre d'Utilisateurs</Label>
              <Input
                type="number"
                value={formData.maxUsers}
                onChange={(e) => setFormData({ ...formData, maxUsers: parseInt(e.target.value) })}
                min="1"
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <FiCalendar /> Date de Début
              </Label>
              <Input
                type="date"
                value={formData.validFrom}
                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <FiCalendar /> Date d'Expiration
              </Label>
              <Input
                type="date"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
              />
            </FormGroup>
          </FormGrid>

          <FormGroup style={{ marginTop: theme.spacing.lg }}>
            <Label>Fonctionnalités Incluses</Label>
            <FeaturesGrid>
              {availableFeatures.map(feature => (
                <FeatureCheckbox key={feature.id}>
                  <input
                    type="checkbox"
                    checked={formData.features.includes(feature.id)}
                    onChange={() => toggleFeature(feature.id)}
                  />
                  {feature.label}
                </FeatureCheckbox>
              ))}
            </FeaturesGrid>
          </FormGroup>

          <GenerateButton
            onClick={handleGenerate}
            disabled={isGenerating || !formData.clientId || !formData.clientName}
          >
            {isGenerating ? (
              <>
                <FiRefreshCw style={{ animation: 'spin 1s linear infinite' }} />
                Génération en cours...
              </>
            ) : (
              <>
                <FiKey /> Générer la Licence
              </>
            )}
          </GenerateButton>
        </FormSection>

        {generatedLicense && (
          <FormSection>
            <SectionTitle>
              <FiCheck /> Licence Générée avec Succès
            </SectionTitle>

            <LicenseResult>
              <LicenseKeyDisplay>
                <InfoLabel style={{ textAlign: 'center', marginBottom: theme.spacing.md }}>
                  CLÉ DE LICENCE
                </InfoLabel>
                <LicenseKeyText>{generatedLicense.licenseKey}</LicenseKeyText>
              </LicenseKeyDisplay>

              <ActionButtons>
                <ActionButton onClick={handleCopy}>
                  {copied ? <FiCheck /> : <FiCopy />}
                  {copied ? 'Copié !' : 'Copier'}
                </ActionButton>
                <ActionButton onClick={handleDownload}>
                  <FiDownload /> Télécharger
                </ActionButton>
                <ActionButton onClick={() => alert('Email envoyé au client')}>
                  📧 Envoyer au Client
                </ActionButton>
              </ActionButtons>

              <LicenseInfo>
                <InfoItem>
                  <InfoLabel>Client</InfoLabel>
                  <InfoValue>{generatedLicense.clientName}</InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>Plan</InfoLabel>
                  <InfoValue style={{ textTransform: 'uppercase' }}>
                    {generatedLicense.plan}
                  </InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>Utilisateurs Max</InfoLabel>
                  <InfoValue>{generatedLicense.maxUsers}</InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>Validité</InfoLabel>
                  <InfoValue>
                    {new Date(generatedLicense.validFrom).toLocaleDateString('fr-FR')} - {' '}
                    {new Date(generatedLicense.validUntil).toLocaleDateString('fr-FR')}
                  </InfoValue>
                </InfoItem>
              </LicenseInfo>

              <SuccessMessage>
                <FiCheckCircle />
                Licence créée et prête à être activée par le client
              </SuccessMessage>
            </LicenseResult>
          </FormSection>
        )}
      </Container>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </DashboardLayout>
  );
};
